/**
 * Organization module service (Wave 3.0 skeleton).
 *
 * Mirrors the /v1/organization/* surface of svdy-platform. Covers the 13
 * Organization pages in svdy-platform-web plus multi-org bootstrap
 * endpoints (which are under /v1 root, not /v1/organization).
 *
 * Types are intentionally `unknown` for now — Wave 3.0 ships the HTTP
 * surface, Wave 3.1 will either (a) backfill hand-written domain types
 * when consumed page-by-page, or (b) adopt `openapi-typescript` generation
 * from svdy-platform's OpenAPI schema.
 */

import { http, type PlatformRequestConfig } from './client';
import type { Schema } from './types';
import { downloadBlob } from '../utils/downloadFile';
import { activeLocale } from '../utils/datetime';

// ---------------------------------------------------------------------------
// Multi-org bootstrap (no X-Organization-Id header — these ARE the lookup
// endpoints that resolve which org to use)
// ---------------------------------------------------------------------------

const CROSS_ORG: PlatformRequestConfig = { skipOrgHeader: true };

export type OrganizationSummary = Schema<'OrganizationSummary'>;
export type OrganizationCreatePayload = Schema<'OrganizationCreate'>;

export function listMyOrganizations() {
  return http.get<OrganizationSummary[]>('/v1/me/organizations', CROSS_ORG);
}

export function createOrganization(data: OrganizationCreatePayload) {
  return http.post<OrganizationProfile>('/v1/organizations', data, CROSS_ORG);
}

// ---------------------------------------------------------------------------
// Organization profile
// ---------------------------------------------------------------------------

export type OrganizationProfile = Schema<'OrganizationProfileResponse'>;
export type OrganizationProfileUpdate = Schema<'OrganizationProfileUpdate'>;

export const profileApi = {
  get: () => http.get<OrganizationProfile>('/v1/organization/profile'),
  update: (data: OrganizationProfileUpdate) =>
    http.patch<OrganizationProfile>('/v1/organization/profile', data),
  /** Wave 7.2: OrganizationOverview dashboard widget support. */
  stats: () =>
    http.get<Schema<'OrganizationStatsResponse'>>('/v1/organization/stats'),
  /**
   * HANDOFF_2026-05-25 Bug 11a — wire logo upload to the existing
   * backend endpoint (svdy-platform organization/router.py
   * upload_logo). Pre-fix the OrganizationProfile page just kept the
   * file in a local data: URL and refresh wiped it.
   *
   * Backend persists the logo to S3 (prod) / local FS (dev), enforces
   * 2 MB cap, accepts JPEG/PNG/SVG/WebP. Returns the full updated
   * profile so the caller can swap state in one call.
   */
  uploadLogo: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<OrganizationProfile>(
      '/v1/organization/profile/logo',
      fd,
      // Don't set Content-Type — axios + FormData computes the
      // multipart boundary correctly only when we leave it alone.
    );
  },
};

// ---------------------------------------------------------------------------
// Branches
// ---------------------------------------------------------------------------

export type BranchNode = Schema<'BranchResponse'>;
export type BranchCreate = Schema<'BranchCreate'>;
export type BranchUpdate = Schema<'BranchUpdate'>;

export const branchesApi = {
  list: () => http.get<BranchNode[]>('/v1/organization/branches'),
  create: (data: BranchCreate) =>
    http.post<BranchNode>('/v1/organization/branches', data),
  update: (id: string, data: BranchUpdate) =>
    http.patch<BranchNode>(`/v1/organization/branches/${id}`, data),
  delete: (id: string) =>
    http.delete<void>(`/v1/organization/branches/${id}`),
};

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export type LocationNode = Schema<'LocationResponse'>;
export type LocationCreate = Schema<'LocationCreate'>;
export type LocationUpdate = Schema<'LocationUpdate'>;

export const locationsApi = {
  list: (branchId?: string) =>
    http.get<LocationNode[]>(
      `/v1/organization/locations${branchId ? `?branch_id=${branchId}` : ''}`,
    ),
  create: (data: LocationCreate) =>
    http.post<LocationNode>('/v1/organization/locations', data),
  update: (id: string, data: LocationUpdate) =>
    http.patch<LocationNode>(`/v1/organization/locations/${id}`, data),
  delete: (id: string) =>
    http.delete<void>(`/v1/organization/locations/${id}`),
};

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

/** Tree-form response: each node carries a `children` array. */
export type DepartmentNode = Schema<'DepartmentResponse'>;
export type DepartmentCreate = Schema<'DepartmentCreate'>;
export type DepartmentUpdate = Schema<'DepartmentUpdate'>;

export const departmentsApi = {
  list: (tree?: boolean) =>
    http.get<DepartmentNode[]>(
      `/v1/organization/departments${tree ? '?tree=true' : ''}`,
    ),
  create: (data: DepartmentCreate) =>
    http.post<DepartmentNode>('/v1/organization/departments', data),
  update: (id: string, data: DepartmentUpdate) =>
    http.patch<DepartmentNode>(`/v1/organization/departments/${id}`, data),
  delete: (id: string) =>
    http.delete<void>(`/v1/organization/departments/${id}`),
};

// ---------------------------------------------------------------------------
// Bulk import (ORG_IMPORT_DESIGN_2026-08-06 slice 1 — departments)
// ---------------------------------------------------------------------------

export type ImportReport = Schema<'ImportReport'>;
export type ImportRowPlan = Schema<'ImportRowPlan'>;
export type ImportFinding = Schema<'ImportFinding'>;
export type ImportCommitResult = Schema<'ImportCommitResult'>;
export type ImportBatchSummary = Schema<'ImportBatchSummary'>;
export type ImportUndoResult = Schema<'ImportUndoResult'>;

/**
 * The download cannot be a plain `<a href>`: the endpoint is authenticated and
 * org-scoped, and an anchor sends neither the bearer token nor
 * `X-Organization-Id`. So it goes through the same client as everything else and
 * the bytes are handed to the browser as an object URL.
 */
async function downloadCsv(path: string, filename: string): Promise<void> {
  const blob = await http.get<Blob>(path, { responseType: 'blob' });
  // The object-URL dance, including the next-tick revoke this function used to own,
  // now lives in `utils/downloadFile`. It had been copied into three pages WITHOUT
  // the revoke timing, so the knowledge existed here and the copies were broken.
  downloadBlob(blob, filename);
}

/**
 * Which sheet an import dialog is driving.
 *
 * The wire value AND the i18n discriminator, deliberately one string: a second
 * mapping from "which page am I on" to "which endpoint" is the drift svdy-rules
 * §10.5b describes, and the backend already names the entity in every report and
 * every ledger row.
 */
export type ImportEntity = 'departments' | 'positions' | 'workers';

/** `positions.csv` — also the filename the backend's Content-Disposition sets. */
const IMPORT_FILENAME: Record<ImportEntity, string> = {
  departments: 'departments.csv',
  positions: 'positions.csv',
  workers: 'workers.csv',
};

/**
 * The query key each template endpoint reads to narrow the file to a selection.
 *
 * Derived per entity rather than hardcoded to `worker_ids`, which is what it was
 * until 2026-08-09 — when only the workers endpoint accepted a filter. Sending
 * `worker_ids` to the departments endpoint would be IGNORED and the admin would
 * silently receive the whole organization while the button said "Export" next to an
 * active filter. A wrong file that looks right is worse than an error.
 */
const IMPORT_ID_PARAM: Record<ImportEntity, string> = {
  departments: 'department_ids',
  positions: 'position_ids',
  workers: 'worker_ids',
};

/**
 * One import client for every sheet.
 *
 * Parameterised rather than copied when positions arrived: two copies of the
 * three-call flow would let one drift — and the flow is where the guarantees live
 * (the token is over the PLAN, the file is re-sent rather than cached).
 */
export function importApi(entity: ImportEntity) {
  const base = `/v1/organization/import/${entity}`;
  return {
    entity,
    /**
     * @param example true for a blank sheet with one example row; the default is
     *   the organization's current rows, which IS the template (§2.2).
     * @param workerIds workers sheet only — narrows the file to a selection, which
     *   is what keeps the Workers page's filter-aware export a real capability
     *   after §10's one-file merge.
     *
     * The admin's UI language rides along automatically. It is read HERE rather than
     * passed by each caller: §11.3.1 puts the valid FK values in the downloaded file
     * and nowhere else, so a caller that forgot the parameter would hand a Japanese
     * admin the one artefact they cannot read — and there is no gate that would
     * notice, because a CSV's comment block is invisible to `localeParity` and to
     * every scan of `.tsx`.
     */
    downloadTemplate: (example = false, ids?: string[]) => {
      const params = new URLSearchParams();
      if (example) params.set('example', 'true');
      if (ids && ids.length > 0) {
        params.set(IMPORT_ID_PARAM[entity], ids.join(','));
      }
      // `activeLocale()`, not `i18next.language` directly: that helper already
      // exists for exactly this reason (the 2026-07-28 locale bug, where sixty
      // call sites read the BROWSER's language instead of the one the admin
      // picked) and it carries the version-specific typing cast.
      params.set('lang', activeLocale());
      const query = params.toString();
      return downloadCsv(
        `${base}/template.csv${query ? `?${query}` : ''}`,
        IMPORT_FILENAME[entity],
      );
    },

    /**
     * The dry run. Writes nothing; returns the diff and a token when clean.
     *
     * `createMissing` is the workers sheet's explicit press on "create these N and
     * continue" (§8.1). It clears ONLY the creatable unknown-reference findings.
     */
    validate: (file: File, createMissing = false) => {
      const form = new FormData();
      form.append('file', file);
      if (createMissing) form.append('create_missing', 'true');
      return http.post<ImportReport>(`${base}/validate`, form);
    },

    /**
     * Applies. The SAME file is sent again with the token — the server re-reads it
     * and refuses when the diff has moved, so there is no server-side cache whose
     * eviction could make Apply silently stop working.
     *
     * `createMissing` must match the validate that produced the token: the flag is
     * part of the fingerprint, so a plain Apply cannot create labels the admin
     * never agreed to.
     */
    commit: (
      file: File,
      token: string,
      opts: { createMissing?: boolean; sendInvitations?: boolean } = {},
    ) => {
      const form = new FormData();
      form.append('token', token);
      form.append('file', file);
      if (opts.createMissing) form.append('create_missing', 'true');
      if (opts.sendInvitations) form.append('send_invitations', 'true');
      return http.post<ImportCommitResult>(`${base}/commit`, form);
    },

    /** Shared across sheets; `entity` filters to this one. */
    batches: (limit = 20) =>
      http.get<ImportBatchSummary[]>(
        `/v1/organization/import/batches?limit=${limit}&entity=${entity}`,
      ),

    undo: (batchId: string) =>
      http.post<ImportUndoResult>(
        `/v1/organization/import/batches/${batchId}/undo`,
      ),
  };
}

export const departmentImportApi = importApi('departments');
export const positionImportApi = importApi('positions');
export const workerImportApi = importApi('workers');

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

export type PositionResponse = Schema<'PositionResponse'>;
export type PositionCreate = Schema<'PositionCreate'>;
export type PositionUpdate = Schema<'PositionUpdate'>;

export const positionsApi = {
  list: () => http.get<PositionResponse[]>('/v1/organization/positions'),
  create: (data: PositionCreate) =>
    http.post<PositionResponse>('/v1/organization/positions', data),
  update: (id: string, data: PositionUpdate) =>
    http.patch<PositionResponse>(`/v1/organization/positions/${id}`, data),
  delete: (id: string) =>
    http.delete<void>(`/v1/organization/positions/${id}`),
};

// ---------------------------------------------------------------------------
// Employment types
// ---------------------------------------------------------------------------

export type EmploymentTypeResponse = Schema<'EmploymentTypeResponse'>;
export type EmploymentTypeCreate = Schema<'EmploymentTypeCreate'>;
export type EmploymentTypeUpdate = Schema<'EmploymentTypeUpdate'>;

export const employmentTypesApi = {
  list: () =>
    http.get<EmploymentTypeResponse[]>('/v1/organization/employment-types'),
  create: (data: EmploymentTypeCreate) =>
    http.post<EmploymentTypeResponse>(
      '/v1/organization/employment-types',
      data,
    ),
  update: (id: string, data: EmploymentTypeUpdate) =>
    http.patch<EmploymentTypeResponse>(
      `/v1/organization/employment-types/${id}`,
      data,
    ),
  delete: (id: string) =>
    http.delete<void>(`/v1/organization/employment-types/${id}`),
};

// ---------------------------------------------------------------------------
// Workers
// ---------------------------------------------------------------------------

export type WorkerResponse = Schema<'WorkerResponse'>;
export type WorkerCreate = Schema<'WorkerCreate'>;
export type WorkerUpdate = Schema<'WorkerUpdate'>;

/** ADR-049 axis 1 — the employment vocabulary, taken from the generated schema so it cannot
 *  drift from the backend Literal. `former` and `inactive` were retired on 2026-09-02. */
export type WorkerStatus = NonNullable<WorkerResponse['status']>;

export const workersApi = {
  /** ADR-049. `deleted` selects the AXIS-2 view: false (default) = live records,
   *  true = only removed ones, which is what makes `restore` reachable. */
  list: (status?: WorkerStatus, deleted = false) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (deleted) params.set('deleted', 'true');
    const query = params.toString();
    return http.get<WorkerResponse[]>(
      `/v1/organization/workers${query ? `?${query}` : ''}`,
    );
  },
  get: (id: string) =>
    http.get<WorkerResponse>(`/v1/organization/workers/${id}`),
  create: (data: WorkerCreate) =>
    http.post<WorkerResponse>('/v1/organization/workers', data),
  update: (id: string, data: WorkerUpdate) =>
    http.patch<WorkerResponse>(`/v1/organization/workers/${id}`, data),
  /**
   * Removes the RECORD (ADR-049 axis 2) — it does NOT end employment, and the backend
   * REFUSES with 400 while employment is live. Returns the row so the caller can see
   * `deleted_at`; it used to return a worker whose `status` had been changed to `former`,
   * a value no list read, so the person silently vanished.
   */
  delete: (id: string) =>
    http.delete<WorkerResponse>(`/v1/organization/workers/${id}`),
  /** Undo a record deletion. Employment status is untouched (ADR-049 rule 4). */
  restore: (id: string) =>
    http.post<WorkerResponse>(`/v1/organization/workers/${id}/restore`, {}),
  bulkUpdate: (data: { worker_ids: string[]; update: Record<string, string | null> }) =>
    http.patch<{ updated: number; failed: number; errors: string[] }>('/v1/organization/workers/bulk', data),

  /**
   * HANDOFF_2026-05-25 Bug 10 — admin requests an email change for
   * a worker. Backend stores a 24h single-use token in Redis + sends
   * a confirmation link to the new email + a notification to the
   * old email. Worker clicks the link to apply the change.
   *
   * 201 on accepted; 400 on validation error (same email, taken in
   * org, malformed). The change DOES NOT take effect until the
   * worker clicks the link in their new inbox.
   */
  requestEmailChange: (workerId: string, new_email: string) =>
    http.post<{ request_id: string; expires_at: string }>(
      `/v1/organization/workers/${workerId}/email-change-request`,
      { new_email },
    ),

  /**
   * GDPR review (S7 §2.2). Workers self-request deletion via
   * /v1/me/account-deletion-request, which sets
   * Worker.deletion_requested_at = now(); the retention purge cron
   * picks up rows where deletion_requested_at + 30 days <= now()
   * and hard-anonymizes them. Admins use these endpoints during
   * the 30-day grace to triage / cancel mistaken requests.
   */
  gdpr: {
    listPendingDeletions: () =>
      http.get<WorkerResponse[]>(
        '/v1/organization/workers/gdpr/pending-deletions',
      ),
    cancelDeletion: (workerId: string) =>
      http.post<WorkerResponse>(
        `/v1/organization/workers/${workerId}/gdpr/cancel-deletion`,
      ),
  },
  /**
   * Wave 7.2: TodayBirthday widget support.
   *
   * Backend accepts `?date=YYYY-MM-DD` (ISO) and defaults to server
   * today when the query is omitted. Previously this wrapper literally
   * sent `?date=today` (the string) which 422'd pydantic. Callers that
   * want "today" should now pass no argument.
   */
  birthdays: (date?: string) =>
    http.get<Schema<'BirthdaysResponse'>>(
      date
        ? `/v1/organization/workers/birthdays?date=${date}`
        : '/v1/organization/workers/birthdays',
    ),
};

// ---------------------------------------------------------------------------
// Document types
// ---------------------------------------------------------------------------

export type DocumentTypeResponse = Schema<'DocumentTypeResponse'>;
export type DocumentTypeCreate = Schema<'DocumentTypeCreate'>;
export type DocumentTypeUpdate = Schema<'DocumentTypeUpdate'>;
export type DocumentTypeChangePreviewRequest = Schema<'DocumentTypeChangePreviewRequest'>;
export type DocumentTypeChangePreview = Schema<'DocumentTypeChangePreview'>;
export type RequiredDocumentSummary = Schema<'RequiredDocumentSummary'>;
export type RequiredDocumentState = Schema<'RequiredDocumentState'>;

/**
 * ADR-044 F1 — the READ surface that makes `is_required` true.
 *
 * Before this, the flag was enforced in exactly ONE place in the whole product: a
 * client-side `validateForm()` on the Add Worker form, in create mode only. Nothing
 * on the server could answer "who is out of compliance", while a production tenant
 * had ticked the box and two of their workers had no Passport on file.
 */
export const requiredDocumentsApi = {
  /** Per-organization three-state counts. */
  summary: () =>
    http.get<RequiredDocumentSummary>(
      '/v1/organization/required-documents/summary',
    ),
  /** Per worker: what they still owe, and by when. */
  forWorker: (workerId: string) =>
    http.get<RequiredDocumentState[]>(
      `/v1/organization/workers/${workerId}/required-documents`,
    ),
};

export const documentTypesApi = {
  list: () =>
    http.get<DocumentTypeResponse[]>('/v1/organization/document-types'),
  create: (data: DocumentTypeCreate) =>
    http.post<DocumentTypeResponse>('/v1/organization/document-types', data),
  update: (id: string, data: DocumentTypeUpdate) =>
    http.patch<DocumentTypeResponse>(
      `/v1/organization/document-types/${id}`,
      data,
    ),
  delete: (id: string) =>
    http.delete<void>(`/v1/organization/document-types/${id}`),
  /**
   * ADR-044 §7.5 — what a proposed edit WOULD do, before it is saved. Writes nothing.
   *
   * Expanding the scope re-introduces the instant-debt problem §6.1 rejected, and
   * `effective_from` does not catch it because the date was never moved: one dropdown
   * change, N instantly overdue records. So the admin sees "Adding Contract will mark
   * 3 workers overdue immediately" — with the names — and decides with the number in
   * front of them instead of discovering it afterwards.
   */
  previewChange: (id: string, data: DocumentTypeChangePreviewRequest) =>
    http.post<DocumentTypeChangePreview>(
      `/v1/organization/document-types/${id}/preview-change`,
      data,
    ),
  /**
   * Download a manifest CSV: one row per active worker × the
   * latest upload of this doc type. Returns the raw text body so
   * the caller can wrap it in a Blob + trigger a browser download.
   * (The endpoint streams text/csv, so we override responseType.)
   */
  manifestCsv: (id: string) =>
    http.get<string>(
      `/v1/organization/document-types/${id}/manifest.csv`,
      { responseType: 'text', transformResponse: (v) => v as string },
    ),
};

// ---------------------------------------------------------------------------
// Worker documents (per-worker uploads). Upload endpoint exists but
// requires a file body; no S3 signed-URL flow wired in the UI yet, so the
// frontend is read-only for now (list + view) and the upload button shows
// a "Coming soon" blocker.
// ---------------------------------------------------------------------------

export type WorkerDocumentResponse = Schema<'WorkerDocumentResponse'>;

export const workerDocumentsApi = {
  listForWorker: (workerId: string) =>
    http.get<WorkerDocumentResponse[]>(
      `/v1/organization/workers/${workerId}/documents`,
    ),
  /**
   * Upload a document file for a worker (P1.2 Phase B item 6).
   * The backend accepts ``document_type_id`` as a query string and
   * the file as ``multipart/form-data`` (field name 'file').
   *
   * Used by the Add Worker dialog to attach required documents
   * immediately after the worker row is created via invite_worker —
   * the worker_id from the invite response chains straight in here.
   */
  upload: (workerId: string, documentTypeId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<WorkerDocumentResponse>(
      `/v1/organization/workers/${workerId}/documents?document_type_id=${encodeURIComponent(documentTypeId)}`,
      fd,
      // Let axios infer the multipart boundary; do NOT set
      // Content-Type manually — that's a recipe for boundary
      // collisions.
    );
  },
  delete: (docId: string) =>
    http.delete<void>(
      `/v1/organization/documents/${docId}`,
    ),
};

// ---------------------------------------------------------------------------
// Face templates (per-worker biometric) + punch-device authorization.
// The punch-device response deliberately omits the stable device identifier;
// the admin only needs status, timing and the audited reset action.
// ---------------------------------------------------------------------------

export type PunchDeviceBinding = Schema<'PunchDeviceBindingResponse'>;
export type PunchDeviceReset = Schema<'PunchDeviceResetRequest'>;

export const faceTemplatesApi = {
  /** Upload a face image for a worker. Stored via the configured
   *  STORAGE_BACKEND (local in dev, S3 in prod). Persists the
   *  resulting URL on Worker.face_template_url. */
  upload: (workerId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post(`/v1/organization/workers/${workerId}/face-template`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /** Delete the face template for a given worker. */
  delete: (workerId: string) =>
    http.delete<void>(
      `/v1/organization/workers/${workerId}/face-template`,
    ),
  /** Issue 4 (Path C 2026-05-22) — invite worker to self-upload via
   *  notification. Creates a Notification row that links to
   *  /me/face-template; backend fanout dispatches in-app + email
   *  + push per the worker's NotificationPreference.channels. Used
   *  by the FaceTemplates page when admin wants the worker to
   *  self-onboard rather than waiting for admin upload. */
  inviteSelfUpload: (workerId: string) =>
    http.post<{
      notification_id: string;
      worker_id: string;
      worker_email: string | null;
      message: string;
    }>(`/v1/organization/workers/${workerId}/face-template/invite-self-upload`),
  punchDevice: {
    get: (workerId: string) =>
      http.get<PunchDeviceBinding>(
        `/v1/attendance/punch-devices/${workerId}`,
      ),
    reset: (workerId: string, data: PunchDeviceReset = {}) =>
      http.post<PunchDeviceBinding>(
        `/v1/attendance/punch-devices/${workerId}/reset`,
        data,
      ),
  },
};

// ---------------------------------------------------------------------------
// Org chart
// ---------------------------------------------------------------------------

export type OrgChartNode = Schema<'OrgChartNode'>;

export const orgChartApi = {
  get: () => http.get<OrgChartNode[]>('/v1/organization/chart'),
};

// ---------------------------------------------------------------------------
// Events & announcements
// ---------------------------------------------------------------------------

export type EventResponse = Schema<'EventResponse'>;
export type EventCreate = Schema<'EventCreate'>;
export type EventUpdate = Schema<'EventUpdate'>;

export const eventsApi = {
  list: () => http.get<EventResponse[]>('/v1/organization/events'),
  create: (data: EventCreate) =>
    http.post<EventResponse>('/v1/organization/events', data),
  update: (id: string, data: EventUpdate) =>
    http.patch<EventResponse>(`/v1/organization/events/${id}`, data),
  delete: (id: string) =>
    http.delete<void>(`/v1/organization/events/${id}`),
};

export type AnnouncementResponse = Schema<'AnnouncementResponse'>;
export type AnnouncementCreate = Schema<'AnnouncementCreate'>;
export type AnnouncementUpdate = Schema<'AnnouncementUpdate'>;

export const announcementsApi = {
  list: () =>
    http.get<AnnouncementResponse[]>('/v1/organization/announcements'),
  create: (data: AnnouncementCreate) =>
    http.post<AnnouncementResponse>('/v1/organization/announcements', data),
  update: (id: string, data: AnnouncementUpdate) =>
    http.patch<AnnouncementResponse>(
      `/v1/organization/announcements/${id}`,
      data,
    ),
  delete: (id: string) =>
    http.delete<void>(`/v1/organization/announcements/${id}`),
};
